import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, FileText, Link2 } from 'lucide-react';
import type { AuditLogItem, ListAuditQuery } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { useSession } from '@/lib/auth';
import { auditListQueryOptions } from '@/lib/audit';
import { datetimePickerHandlers } from '@/lib/datetime-picker';
import { hasPermission } from '@/lib/permissions';
import { usersListQueryOptions } from '@/lib/users';

interface AuditSearch {
  page?: number;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  userId?: string;
  from?: string;
  to?: string;
}

const RESOURCE_PILLS: { value: string | undefined; label: string }[] = [
  { value: undefined, label: 'ทั้งหมด' },
  { value: 'auth', label: 'Auth' },
  { value: 'post', label: 'Post' },
  { value: 'user', label: 'User' },
  { value: 'media', label: 'Media' },
  { value: 'category', label: 'Category' },
  { value: 'tag', label: 'Tag' },
  { value: 'setting', label: 'Setting' },
  // Below this line: less-frequently used but valid resourceTypes
  // emitted by their respective admin routes. Order roughly by
  // frequency so the visual hierarchy reads right.
  { value: 'comment', label: 'Comment' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'feed_source', label: 'Feed' },
  { value: 'role', label: 'Role' },
];

/**
 * Forensically-interesting one-click filters. Picked for high signal
 * per click: brute-force probes, destructive actions, RBAC changes,
 * the new unlock path. Tint color hints at category (red = security
 * concern, amber = privileged action, slate = neutral observation).
 */
const QUICK_ACTIONS: { code: string; label: string; tint: string }[] = [
  {
    code: 'auth.login_failed',
    label: 'login ล้มเหลว',
    tint: 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300',
  },
  {
    code: 'user.privilege_escalation_blocked',
    label: 'escalation blocked',
    tint: 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300',
  },
  {
    code: 'post.delete',
    label: 'post.delete',
    tint: 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
  },
  {
    code: 'role.permissions_update',
    label: 'RBAC change',
    tint: 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
  },
  {
    code: 'user.unlock',
    label: 'unlock',
    tint: 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
  },
];

export const Route = createFileRoute('/_authenticated/audit/')({
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    page: typeof search.page === 'number' ? search.page : undefined,
    action: typeof search.action === 'string' ? search.action : undefined,
    resourceType: typeof search.resourceType === 'string' ? search.resourceType : undefined,
    resourceId: typeof search.resourceId === 'string' ? search.resourceId : undefined,
    userId: typeof search.userId === 'string' ? search.userId : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  component: AuditPage,
});

/**
 * `<input type="datetime-local">` only accepts `YYYY-MM-DDTHH:mm`.
 * The URL stores ISO strings (with timezone) so we round-trip via
 * Date: ISO → local date → trim to minute precision in local zone.
 */
function toLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse — datetime-local string → ISO with the browser's local
 *  timezone offset baked in. Empty input clears the bound. */
function fromLocalInput(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

interface DatePreset {
  label: string;
  compute: () => { from: string; to: string };
}

const DATE_PRESETS: DatePreset[] = [
  {
    // Sharpest preset for live incident response — "is this happening
    // RIGHT NOW?" Pairs naturally with the auth.login_failed quick
    // chip for brute-force triage.
    label: '1 ชม. ล่าสุด',
    compute: () => {
      const end = new Date();
      const start = new Date(end.getTime() - 60 * 60 * 1000);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: 'วันนี้',
    compute: () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: 'เมื่อวาน',
    compute: () => {
      const end = new Date();
      end.setHours(0, 0, 0, 0);
      const start = new Date(end);
      start.setDate(start.getDate() - 1);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: '7 วันล่าสุด',
    compute: () => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 7);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: '30 วันล่าสุด',
    compute: () => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 30);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
];

/**
 * Serialize the current AuditSearch into a query string compatible
 * with the CSV export endpoint. Skips empty values + the `page`
 * field (export ignores pagination anyway).
 */
function exportQuery(search: AuditSearch): string {
  const params = new URLSearchParams();
  if (search.action) params.set('action', search.action);
  if (search.resourceType) params.set('resourceType', search.resourceType);
  if (search.resourceId) params.set('resourceId', search.resourceId);
  if (search.userId) params.set('userId', search.userId);
  if (search.from) params.set('from', search.from);
  if (search.to) params.set('to', search.to);
  return params.toString();
}

// Used in the audit list resourceId column. Post ids are 13-digit
// timestamps; uuids are 36 chars — both blow out the column at full
// width. 8…4 keeps the prefix readable and the suffix useful for
// disambiguation.
function truncateId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function actionColor(action: string): string {
  if (action.includes('delete'))
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (action.includes('create') || (action.includes('login') && !action.includes('failed')))
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (action.includes('publish'))
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  if (action.includes('failed'))
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
}

function AuditPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const filter: Partial<ListAuditQuery> = {
    page: search.page ?? 1,
    perPage: 50,
    action: search.action,
    resourceType: search.resourceType,
    resourceId: search.resourceId,
    userId: search.userId,
    from: search.from,
    to: search.to,
  };
  const query = useQuery(auditListQueryOptions(filter));
  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));
  const currentPage = filter.page ?? 1;

  // Backend gates GET /admin/users on user.update — only roles that can
  // fetch the list get the actor dropdown. Anyone with audit.read who
  // lacks user.update can still filter via the URL ?userId=<id>.
  const canListUsers = hasPermission(user, 'user.update');
  const usersQuery = useQuery({
    ...usersListQueryOptions({ perPage: 100 }),
    enabled: canListUsers,
    staleTime: 60_000,
  });
  const users = usersQuery.data?.items ?? [];

  const [actionInput, setActionInput] = useState(search.action ?? '');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const setResource = (value: string | undefined) =>
    void navigate({ search: { ...search, resourceType: value, page: 1 } });
  const goPage = (page: number) => void navigate({ search: { ...search, page } });

  const onActionFilter = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({
      search: { ...search, action: actionInput.trim() || undefined, page: 1 },
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Audit Log
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total} เหตุการณ์` : 'ยังไม่มีเหตุการณ์'}
              {/* Count of active filter dimensions — visible cue so the
                  reader knows the number above is filtered, not the
                  full audit volume. Plays well with the "ล้างทั้งหมด"
                  link below. */}
              {(() => {
                const n = [
                  search.action,
                  search.resourceType,
                  search.resourceId,
                  search.userId,
                  search.from,
                  search.to,
                ].filter(Boolean).length;
                return n > 0 ? (
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    กรอง {n}
                  </span>
                ) : null;
              })()}
              {' · '}บันทึก action สำคัญที่ admin ทำในระบบ (อ่านอย่างเดียว)
            </p>
          </div>
          {/* Native <a> so the download lands as a real file. Cookies
              ride along on cross-site GET because session is
              SameSite=None+Secure; CSRF guard skips GET methods.
              Two buttons rather than a dropdown — both small, both
              the same gesture; dropdown would just add a click. */}
          <div className="flex gap-1">
            {/* Share-link copies the FULL current URL with every active
                filter, so investigators can paste it into a thread for
                a colleague to land on the exact view. SPA route, so
                the link works as long as the recipient has audit.read. */}
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => toast.success('คัดลอกลิงก์รวม filter แล้ว'))
                  .catch(() => {
                    // Clipboard denied — silent.
                  });
              }}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              title="คัดลอกลิงก์หน้านี้ (พร้อม filter)"
            >
              <Link2 className="h-3.5 w-3.5" />
              ลิงก์
            </button>
            <a
              href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/admin/audit-logs/export.csv?${exportQuery(search)}`}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </a>
            <a
              href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/admin/audit-logs/export.json?${exportQuery(search)}`}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Download className="h-3.5 w-3.5" />
              JSON
            </a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {RESOURCE_PILLS.map((p) => {
            const active = search.resourceType === p.value;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => setResource(p.value)}
                className={
                  active
                    ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                    : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                {p.label}
              </button>
            );
          })}
          {/* Resource-id filter chip — only set when arriving from a
              "ดูประวัติทั้งหมด →" deep link (e.g. from PostAuditPanel).
              Clickable X clears just the id while keeping resourceType,
              so the user lands on the broader resource view. */}
          {search.resourceId && (
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-100 px-3 py-1 font-mono text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <span className="truncate">id: {search.resourceId}</span>
              <button
                type="button"
                onClick={() =>
                  void navigate({ search: { ...search, resourceId: undefined, page: 1 } })
                }
                aria-label="ล้าง id"
                className="ml-1 shrink-0 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                ×
              </button>
            </span>
          )}
          {/* Master clear — wipes EVERY filter at once. Useful when
              an investigator has stacked resource + action + user +
              date filters and wants to reset to the unfiltered list.
              Only shown when at least one filter is active. */}
          {(search.action ||
            search.resourceType ||
            search.resourceId ||
            search.userId ||
            search.from ||
            search.to) && (
            <button
              type="button"
              onClick={() => void navigate({ search: {} })}
              className="text-xs text-slate-500 hover:underline"
              title="ล้าง filter ทั้งหมด"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Date range — separate row so the action+user filter row
            doesn't get crowded. Presets navigate to ISO datetimes via
            the validator; clearing both is one button. */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-500">ตั้งแต่</label>
          <input
            type="datetime-local"
            value={toLocalInput(search.from)}
            onChange={(e) =>
              void navigate({
                search: { ...search, from: fromLocalInput(e.target.value), page: 1 },
              })
            }
            {...datetimePickerHandlers}
            className="h-9 cursor-pointer rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
          <label className="text-xs text-slate-500">ถึง</label>
          <input
            type="datetime-local"
            value={toLocalInput(search.to)}
            onChange={(e) =>
              void navigate({
                search: { ...search, to: fromLocalInput(e.target.value), page: 1 },
              })
            }
            {...datetimePickerHandlers}
            className="h-9 cursor-pointer rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                const { from, to } = p.compute();
                void navigate({ search: { ...search, from, to, page: 1 } });
              }}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              {p.label}
            </button>
          ))}
          {(search.from || search.to) && (
            <button
              type="button"
              onClick={() =>
                void navigate({ search: { ...search, from: undefined, to: undefined, page: 1 } })
              }
              className="text-xs text-slate-500 hover:underline"
            >
              ล้างช่วงเวลา
            </button>
          )}
        </div>

        {/* Quick-action chips — high-signal forensic / security events
            that the typical admin reaches for. Click toggles the filter:
            same chip = clear; different chip = jump. Power users still
            have the free-text input below for prefix searches like
            "feed.*" or other actions not listed here. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-500">ด่วน:</span>
          {QUICK_ACTIONS.map((a) => {
            const active = search.action === a.code;
            return (
              <button
                key={a.code}
                type="button"
                onClick={() => {
                  const next = active ? undefined : a.code;
                  setActionInput(next ?? '');
                  void navigate({ search: { ...search, action: next, page: 1 } });
                }}
                className={
                  active
                    ? 'bg-brand-600 rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white'
                    : `rounded-full px-2.5 py-0.5 text-[11px] font-medium ${a.tint}`
                }
                title={a.code}
              >
                {a.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={onActionFilter} className="flex flex-wrap gap-2">
          <Input
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            placeholder="กรอง action (เช่น post.publish)"
            className="max-w-md font-mono"
          />
          <Button type="submit" variant="outline">
            กรอง
          </Button>
          {search.action && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setActionInput('');
                void navigate({ search: { ...search, action: undefined, page: 1 } });
              }}
            >
              ล้าง
            </Button>
          )}
          {canListUsers && (
            <select
              value={search.userId ?? ''}
              onChange={(e) => {
                const nextId = e.target.value || undefined;
                void navigate({ search: { ...search, userId: nextId, page: 1 } });
              }}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              aria-label="กรองตามผู้ทำ"
            >
              <option value="">ทุกผู้ใช้</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          )}
          {/* "ของฉัน" toggle — works even when user.list is missing
              (dropdown is hidden but self-scope is always allowed).
              Toggles userId between undefined and current viewer's id
              so a second click un-scopes. */}
          {user?.id && (
            <Button
              type="button"
              variant={search.userId === user.id ? 'default' : 'outline'}
              size="sm"
              onClick={() =>
                void navigate({
                  search: {
                    ...search,
                    userId: search.userId === user.id ? undefined : user.id,
                    page: 1,
                  },
                })
              }
              title="กรองเหตุการณ์ที่ฉันทำ"
            >
              ของฉัน
            </Button>
          )}
        </form>

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <FileText className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ไม่มีเหตุการณ์ที่ตรงกัน</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                  <tr>
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 font-medium">เวลา</th>
                    <th className="px-4 py-3 font-medium">ผู้ทำ</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Resource</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {items.map((r) => {
                    const isOpen = expanded.has(r.id);
                    const hasMetadata = r.metadata !== null && Object.keys(r.metadata).length > 0;
                    return (
                      <Row
                        key={r.id}
                        item={r}
                        isOpen={isOpen}
                        hasMetadata={hasMetadata}
                        onToggle={() => toggleExpand(r.id)}
                        activeUserId={search.userId}
                        activeResourceId={search.resourceId}
                        meId={user?.id}
                        onActorClick={(userId) =>
                          void navigate({ search: { ...search, userId, page: 1 } })
                        }
                        onResourceClick={(resourceType, resourceId) =>
                          void navigate({
                            search: { ...search, resourceType, resourceId, page: 1 },
                          })
                        }
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => goPage(currentPage - 1)}
                >
                  ก่อนหน้า
                </Button>
                <span className="text-slate-500">
                  หน้า {currentPage} จาก {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => goPage(currentPage + 1)}
                >
                  ถัดไป
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  item: AuditLogItem;
  isOpen: boolean;
  hasMetadata: boolean;
  onToggle: () => void;
  /** Set the userId filter on the parent's search. Hidden when the
   *  list is already filtered to this actor (no-op). Anonymous rows
   *  (no userId) skip rendering the click handler entirely. */
  onActorClick?: (userId: string) => void;
  activeUserId?: string;
  /** Set both resourceType + resourceId filters at once — drills
   *  the audit list into the full history of a specific resource.
   *  Hidden when the list is already filtered to this exact id. */
  onResourceClick?: (resourceType: string, resourceId: string) => void;
  activeResourceId?: string;
  /** Current viewer's id — when it matches `item.userId` the row gets
   *  a "ฉัน" pill so the reader spots their own actions at a glance.
   *  Mirrors the same gesture on /posts and /comments rows. */
  meId?: string;
}

function Row({
  item,
  isOpen,
  hasMetadata,
  onToggle,
  onActorClick,
  activeUserId,
  onResourceClick,
  activeResourceId,
  meId,
}: RowProps) {
  const isMe = meId !== undefined && item.userId === meId;
  return (
    <>
      <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
        <td className="px-2 py-3 align-top">
          {hasMetadata ? (
            <button type="button" onClick={onToggle} aria-label="toggle metadata">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400" />
              )}
            </button>
          ) : null}
        </td>
        <td className="px-4 py-3 align-top text-xs text-slate-500">
          {new Date(item.createdAt).toLocaleString('th-TH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </td>
        <td className="px-4 py-3 align-top">
          {item.userName ? (
            // Click to scope audit to this actor — useful when an
            // investigator sees a row of interest and wants "show me
            // everything else this person did". Hidden when already
            // filtered to this user (no-op).
            item.userId && onActorClick && activeUserId !== item.userId ? (
              <button
                type="button"
                onClick={() => onActorClick(item.userId!)}
                className="hover:text-brand-600 dark:hover:text-brand-400 text-left"
                title="กรอง audit เฉพาะของผู้ใช้นี้"
              >
                <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                  {item.userName}
                  {isMe && (
                    <span className="inline-flex items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      ฉัน
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">{item.userEmail}</div>
              </button>
            ) : (
              <div>
                <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                  {item.userName}
                  {isMe && (
                    <span className="inline-flex items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      ฉัน
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">{item.userEmail}</div>
              </div>
            )
          ) : (
            <span className="text-[11px] italic text-slate-400">anonymous</span>
          )}
        </td>
        <td className="px-4 py-3 align-top">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px] ${actionColor(item.action)}`}
          >
            {item.action}
          </span>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="text-xs">
            <span className="text-slate-700 dark:text-slate-300">{item.resourceType}</span>
            {item.resourceId && (
              <div className="font-mono text-[10px]">
                {/* Primary destination per type: `post` → editor,
                    `feed_source` → inbox (the two resources that have
                    a dedicated editor route). Everything else falls
                    through to a history-drill button that filters the
                    audit list to that exact resource id — useful when
                    investigating "what else happened to this user /
                    category / webhook?". The same drill is available
                    on post/feed_source rows via the existing
                    resourceType pill above. */}
                {item.resourceType === 'post' ? (
                  <Link
                    to="/posts/$postId"
                    params={{ postId: item.resourceId }}
                    className="hover:text-brand-600 dark:hover:text-brand-400 text-slate-400 hover:underline"
                    title={`เปิดบทความ ${item.resourceId}`}
                  >
                    {truncateId(item.resourceId)}
                  </Link>
                ) : item.resourceType === 'feed_source' ? (
                  <Link
                    to="/feed-sources"
                    className="hover:text-brand-600 dark:hover:text-brand-400 text-slate-400 hover:underline"
                    title={`เปิด Inbox ของ feed ${item.resourceId}`}
                  >
                    {truncateId(item.resourceId)}
                  </Link>
                ) : onResourceClick && activeResourceId !== item.resourceId ? (
                  <button
                    type="button"
                    onClick={() => onResourceClick(item.resourceType, item.resourceId!)}
                    className="hover:text-brand-600 dark:hover:text-brand-400 text-slate-400 hover:underline"
                    title={`กรอง audit เฉพาะของ ${item.resourceType} นี้`}
                  >
                    {truncateId(item.resourceId)}
                  </button>
                ) : (
                  <span className="text-slate-400">{truncateId(item.resourceId)}</span>
                )}
              </div>
            )}
          </div>
        </td>
        <td className="px-4 py-3 align-top font-mono text-[11px] text-slate-500">
          {item.ipAddress ? (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(item.ipAddress!).catch(() => {
                  // Clipboard API can fail in non-secure contexts; user
                  // can still triple-click to select. Silent fallback.
                });
              }}
              className="hover:text-brand-600 dark:hover:text-brand-400"
              title="คลิกเพื่อคัดลอก"
            >
              {item.ipAddress}
            </button>
          ) : (
            '—'
          )}
        </td>
      </tr>
      {isOpen && hasMetadata && (
        <tr className="bg-slate-50 dark:bg-slate-800/30">
          <td />
          <td colSpan={5} className="px-4 py-2">
            <pre className="overflow-x-auto rounded bg-slate-100 p-2 text-[11px] dark:bg-slate-900">
              {JSON.stringify(item.metadata, null, 2)}
            </pre>
            {item.userAgent && (
              <p className="mt-1 break-all text-[10px] text-slate-400">UA: {item.userAgent}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
